'use client';

import React, { ReactNode, useEffect } from 'react';

import { classNames } from '@bytel/product-wall/helpers';
import {
    Align,
    Box,
    BoxContent,
    Button,
    ButtonMarkup,
    ButtonVariant,
    Column,
    Columns,
    Container,
    Icon,
    IconName,
    IconSize,
    Text,
    TextLevels,
    TypographyBold,
    TypographyColor,
    View,
} from '@bytel/trilogy-react';

import { HomePackCategory } from '@app-types/home-pack';
import { SimulatedCart } from '@app-types/simulator/virtual-cart';

import { InlinePrice } from '@components/inline-price';

interface StickyBarProps {
    isVisible: boolean;
    currentSimulatedCart: SimulatedCart;
    currentStaticCategory: HomePackCategory;
    href: string;
    onClick?: () => void;
}

export function StickyBar({
    isVisible,
    currentSimulatedCart,
    currentStaticCategory,
    href,
    onClick,
}: Readonly<StickyBarProps>) {
    const bboxPrice = currentSimulatedCart.quotes[0]?.products[1]?.details.price.final ?? 0;
    const bboxName = currentSimulatedCart.quotes[0]?.products[1]?.name;
    const productPrice = currentSimulatedCart.quotes[0]?.products[0]?.details.price.final ?? 0;
    const productName = currentSimulatedCart.quotes[0]?.products[0]?.name;

    useEffect(() => {
        // Position omnikit widget just before the sticky bar container
        window.omnikit = { insertBeforeId: 'sticky-bar' };
    }, []);

    return (
        <View className={classNames('sticky-bar sticky-bottom', isVisible ? 'visible' : '')} id='sticky-bar'>
            <Box>
                <BoxContent>
                    <Container>
                        <Columns verticalAlign={Align.CENTER} marginless>
                            <Column>
                                <Columns>
                                    <Column narrow>
                                        <StickyItem
                                            icon={currentStaticCategory.icon}
                                            title={productName}
                                            subtitle={
                                                <InlinePrice
                                                    typo={[TypographyBold.TEXT_WEIGHT_NORMAL]}
                                                    amount={productPrice}
                                                />
                                            }
                                        />
                                    </Column>
                                    <Column narrow>
                                        <StickyItem
                                            icon={IconName.BOX_WIFI_6}
                                            title={bboxName}
                                            subtitle={
                                                <InlinePrice
                                                    typo={[TypographyBold.TEXT_WEIGHT_NORMAL]}
                                                    amount={bboxPrice}
                                                    period='mois'
                                                />
                                            }
                                        />
                                    </Column>
                                </Columns>
                            </Column>
                            <Column narrow>
                                <Button
                                    markup={ButtonMarkup.A}
                                    href={href}
                                    onClick={onClick}
                                    variant={ButtonVariant.CONVERSION}
                                >
                                    Tester mon éligibilité
                                </Button>
                            </Column>
                        </Columns>
                    </Container>
                </BoxContent>
            </Box>
        </View>
    );
}

function StickyItem({ icon, title, subtitle }: Readonly<{ icon: IconName; title: ReactNode; subtitle?: ReactNode }>) {
    return (
        <Columns mobile verticalAlign={Align.CENTER}>
            <Column narrow>
                <Icon name={icon} size={IconSize.MEDIUM} className='w-fit' />
            </Column>
            <Column>
                <Text
                    marginless
                    level={TextLevels.ONE}
                    typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_MAIN]}
                >
                    {title}
                </Text>
                {subtitle && (
                    <Text level={TextLevels.TWO} marginless className='is-hidden-mobile'>
                        {subtitle}
                    </Text>
                )}
            </Column>
            <Column narrow>
                {subtitle && (
                    <Text level={TextLevels.TWO} marginless className='is-hidden-tablet'>
                        {subtitle}
                    </Text>
                )}
            </Column>
        </Columns>
    );
}
